import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {API_ROOT_URL} from "../../envs/_current/config";
import {LocalContext} from "./LocalContext";

const apiInfo = {
    root: API_ROOT_URL,
    path: '/apps/get-configs-by-keys',
    method: 'GET',
    paramsFields: ["keys"]
};

const ALL_KEYS = [
    'is_maintenance',
    'default_channels',
    'readings_book',
    'saints_book',
    'highlighted_channel_id',
    'highlighted_channel_id2',
    'org_types',
    'role_titles',
    'liturgical_seasons',
    'liturgical_event_types',
    'prayers_books',
    'bibles',
    'countries',
    'provinces',
    'wards',
    'dioceses',
    'parishes',
    'categories',
    'hierarchy_categories',
    'document_types',
    'hierarchy_document_types',
    'music_types',
    'hierarchy_music_types',
    'position_titles',
    'archdioceses',
    'deaneries',
    'prayer_methods',
    'org_type_configs',
    'group_type_configs',
];

const AppConfigContext = createContext({});

// ─── Provider ─────────────────────────────────────────────
export function AppConfigProvider({children}) {
    const {lang} = useContext(LocalContext);

    // Cache theo lang: { vi: { parishes: [], ... }, en: { parishes: [], ... } }
    const [cache, setCache] = useState({});

    // Set các key đã pending theo lang, tránh gọi API 2 lần
    // { vi: Set(['parishes', 'dioceses']), en: Set([...]) }
    const pendingKeys = useRef({});

    // const {handleAsyncAction: fetchConfig} = useAsyncAction({
    //     apiInfo,
    //     onSuccess: (__, data) => {
    //         const configs = _.get(data, 'response.data');
    //         if (!configs) return;
    //         console.log(11111111, lang, 2, configs)
    //         setCache(prev => ({
    //             ...prev,
    //             [lang]: {...(prev[lang] || {}), ...configs}
    //         }));
    //     },
    // });
    const fetchConfig = useCallback((keys, currentLang) => {
        const url = `${API_ROOT_URL}/apps/get-configs-by-keys?keys=${keys.join(',')}`;
        fetch(url)
            .then(r => r.json())
            .then(configs => {

                // console.log(1111129292, data)
                // const configs = _.get(data, 'response.data');
                if (!configs) return;
                setCache(prev => ({
                    ...prev,
                    [currentLang]: {...(prev[currentLang] || {}), ...configs}
                }));
            });
    }, []);

    const fetchConfigRef = useRef(fetchConfig);
    fetchConfigRef.current = fetchConfig;

    // requestKeys: chỉ fetch key chưa pending theo lang hiện tại
    const requestKeys = useRef((keys = [], currentLang) => {
        if (!pendingKeys.current[currentLang]) {
            pendingKeys.current[currentLang] = new Set();
        }
        const langPending = pendingKeys.current[currentLang];
        const needed = keys.filter(k => !langPending.has(k));
        if (!needed.length) return;

        needed.forEach(k => langPending.add(k));
        fetchConfig(needed, currentLang);
    });

    // forceRefetch: bỏ qua pending cache, gọi lại API ngay
    // dùng useRef để reference stable, không gây loop khi để trong deps
    const langRef = useRef(lang);
    langRef.current = lang;

    const lastFetchTime = useRef({});
    const forceRefetch = useRef((keys = [], currentLang) => {
        const targetLang = currentLang ?? langRef.current;
        const targetKeys = keys.length ? keys : ALL_KEYS;
        const now = Date.now();

        // Chặn gọi lại trong vòng 500ms cùng key+lang — tránh StrictMode double invoke
        const dedupeKey = `${targetLang}:${targetKeys.join(',')}`;
        if (lastFetchTime.current[dedupeKey] && now - lastFetchTime.current[dedupeKey] < 500) return;
        lastFetchTime.current[dedupeKey] = now;

        if (pendingKeys.current[targetLang]) {
            targetKeys.forEach(k => pendingKeys.current[targetLang].delete(k));
        }
        fetchConfigRef.current(targetKeys, targetLang);
    });

    // Khi đổi lang → fetch lại những key đã có trong cache của lang cũ
    // (vì lang mới chưa có data)
    const prevLang = useRef(lang);
    useEffect(() => {
        if (prevLang.current === lang) return;
        prevLang.current = lang;

        // Lấy tất cả key đang có của lang cũ, fetch lại cho lang mới
        const cachedKeys = Object.keys(cache[lang] || {});
        const allPendingKeys = Object.keys(
            pendingKeys.current[lang]
                ? Object.fromEntries([...pendingKeys.current[lang]].map(k => [k, 1]))
                : {}
        );
        const keysToRefetch = [...new Set([...cachedKeys, ...allPendingKeys])];
        if (keysToRefetch.length) {
            requestKeys.current(keysToRefetch, lang);
        }
    }, [lang]); // eslint-disable-line


    return (
        <AppConfigContext.Provider value={{
            cache,
            lang,
            requestKeys: requestKeys.current,
            forceRefetch: forceRefetch.current,
        }}>
            {children}
        </AppConfigContext.Provider>
    );
}

// ─── Hook ─────────────────────────────────────────────────
export function useAppConfig(...args) {
    // tách options ra nếu arg cuối là object
    const lastArg = args[args.length - 1]
    const hasOptions = lastArg && typeof lastArg === 'object' && !Array.isArray(lastArg)
    const options = hasOptions ? lastArg : {}
    const keyArgs = hasOptions ? args.slice(0, -1) : args

    const { skip = false } = options

    const { cache, lang, requestKeys } = useContext(AppConfigContext)

    const keys = keyArgs.length === 0
        ? ALL_KEYS
        : (Array.isArray(keyArgs[0]) ? keyArgs[0] : keyArgs)
            .map(k => k.trim())
            .filter(Boolean)

    const keysStr = keys.join(',')

    useEffect(() => {
        if (skip) return          // ← không fetch nếu skip=true
        if (keys.length) requestKeys(keys, lang)
    }, [keysStr, lang, skip]) // eslint-disable-line

    const langCache = cache[lang] || {}
    return keys.reduce((acc, key) => {
        acc[key] = langCache[key]
        return acc
    }, {})
}

export default AppConfigContext;

// ─── Hook force refetch ────────────────────────────────────
// Dùng khi cần invalidate sau khi thay đổi dữ liệu, ví dụ:
//   const refetch = useForceRefetchConfig()
//   refetch(['parishes'])          → chỉ parishes
//   refetch(['parishes', 'dioceses'])
//   refetch()                      → toàn bộ ALL_KEYS
export function useForceRefetchConfig() {
    const ctx = useContext(AppConfigContext);
    if (!ctx) throw new Error('useForceRefetchConfig phải dùng bên trong <AppConfigProvider>');
    return ctx.forceRefetch;
}